import React, { useState } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  FormControlLabel,
  Checkbox,
  Snackbar,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  CheckCircle as VerifiedIcon,
  Warning as WarningIcon,
  FormatQuote as CitableIcon,
  Search as SearchIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { GET_VECTOR_DOCUMENTS, GET_VECTOR_DOCUMENT_CATEGORIES, RAG_TEST_SEARCH } from './RagAdminQueries';
import {
  CREATE_VECTOR_DOCUMENT,
  UPDATE_VECTOR_DOCUMENT,
  DELETE_VECTOR_DOCUMENT,
  PROCESS_URLS,
  REGENERATE_EMBEDDING,
  BULK_IMPORT_DOCUMENTS,
} from './RagAdminMutations';
import VectorDocumentForm from './VectorDocumentForm';
import UrlProcessorDialog from './UrlProcessorDialog';
import BulkImportDialog from './BulkImportDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingTop: theme.spacing(8), // Add more top padding to account for admin header
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stats: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    textAlign: 'center',
  },
  filters: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  statusChip: {
    fontWeight: 'bold',
  },
  verifiedIcon: {
    color: theme.palette.success.main,
  },
  warningIcon: {
    color: theme.palette.warning.main,
  },
  categoryChip: {
    margin: theme.spacing(0.5),
  },
  tagChip: {
    margin: theme.spacing(0.25),
    height: 24,
  },
  searchSection: {
    padding: theme.spacing(3),
  },
  searchInput: {
    marginBottom: theme.spacing(2),
  },
  searchResult: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  similarityScore: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  documentBody: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    maxHeight: 100,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

// Alert component for snackbar
const Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;

const RagAdminDashboard = () => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTab, setSelectedTab] = useState(0);
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    verified: null,
    search: '',
  });
  const [formOpen, setFormOpen] = useState(false);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [testSearchLimit, setTestSearchLimit] = useState(10);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  // Queries
  const { data, loading, error, refetch } = useQuery(GET_VECTOR_DOCUMENTS, {
    variables: {
      category: filters.category || null,
      status: filters.status || null,
      verified: filters.verified,
      search: filters.search || null,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    },
  });

  const { data: categoriesData } = useQuery(GET_VECTOR_DOCUMENT_CATEGORIES);

  // Lazy query for test search
  const [executeTestSearch, { data: searchData, loading: searchLoading }] = useLazyQuery(RAG_TEST_SEARCH);

  const handleTestSearch = () => {
    if (testSearchQuery.trim()) {
      executeTestSearch({
        variables: {
          query: testSearchQuery,
          limit: testSearchLimit,
        },
      });
    }
  };

  // Mutations
  const [createDocument] = useMutation(CREATE_VECTOR_DOCUMENT, {
    onCompleted: () => {
      setFormOpen(false);
      refetch();
    },
  });

  const [updateDocument] = useMutation(UPDATE_VECTOR_DOCUMENT, {
    onCompleted: () => {
      setFormOpen(false);
      setSelectedDocument(null);
      refetch();
    },
  });

  const [deleteDocument] = useMutation(DELETE_VECTOR_DOCUMENT, {
    onCompleted: () => {
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
      refetch();
    },
  });

  const [regenerateEmbedding] = useMutation(REGENERATE_EMBEDDING);
  const [processUrls] = useMutation(PROCESS_URLS, {
    onCompleted: () => {
      setUrlDialogOpen(false);
      refetch();
    },
  });
  const [bulkImportDocuments] = useMutation(BULK_IMPORT_DOCUMENTS, {
    onCompleted: () => {
      setBulkImportOpen(false);
      refetch();
    },
  });

  // Handlers
  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPage(0);
  };

  const handleCreateNew = () => {
    setSelectedDocument(null);
    setFormOpen(true);
  };

  const handleEdit = (document) => {
    setSelectedDocument(document);
    setFormOpen(true);
  };

  const handleDelete = (document) => {
    setDocumentToDelete(document);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (documentToDelete) {
      await deleteDocument({ variables: { id: documentToDelete.id } });
    }
  };

  const handleRegenerateEmbedding = async (documentId) => {
    await regenerateEmbedding({ variables: { id: documentId } });
    refetch();
  };

  // Get stats from server response (these are total counts across all documents, not just current page)
  const pagedData = data?.vectorDocumentsPaged;
  const stats = {
    total: pagedData?.totalCount || 0,
    active: pagedData?.activeCount || 0,
    verified: pagedData?.verifiedCount || 0,
    withEmbeddings: pagedData?.withEmbeddingsCount || 0,
    citable: pagedData?.citableCount || 0,
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'primary';
      case 'processing': return 'default';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h4">RAG Document Management</Typography>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={() => setBulkImportOpen(true)}
            style={{ marginRight: 8 }}
          >
            Bulk Import
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<LinkIcon />}
            onClick={() => setUrlDialogOpen(true)}
            style={{ marginRight: 8 }}
          >
            Process URLs
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Add Document
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} className={classes.stats}>
        <Grid item xs={3}>
          <Card>
            <CardContent className={classes.statCard}>
              <Typography variant="h4">{stats.total}</Typography>
              <Typography variant="body2" color="textSecondary">
                Total Documents
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card>
            <CardContent className={classes.statCard}>
              <Typography variant="h4" color="primary">
                {stats.active}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Active
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card>
            <CardContent className={classes.statCard}>
              <Typography variant="h4" style={{ color: '#4caf50' }}>
                {stats.verified}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Verified
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={3}>
          <Card>
            <CardContent className={classes.statCard}>
              <Typography variant="h4" color="secondary">
                {stats.withEmbeddings}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                With Embeddings
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper>
        <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)}>
          <Tab label="All Documents" />
          <Tab label="Pending Review" />
          <Tab label="Categories" />
          <Tab label="Test Search" />
        </Tabs>

        {selectedTab === 0 && (
          <>
            {/* Filters */}
            <Box className={classes.filters} p={2}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={{ minWidth: 200 }}
              />
              <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">All</MenuItem>
                  {categoriesData?.vectorDocumentCategories?.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.verified === true}
                    onChange={(e) =>
                      handleFilterChange('verified', e.target.checked ? true : null)
                    }
                  />
                }
                label="Verified Only"
              />
            </Box>

            {/* Table */}
            <Box className={classes.tableContainer}>
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Paper style={{ padding: '16px', backgroundColor: '#f44336', color: 'white', marginBottom: '16px' }}>
                  <Typography>Error: {error.message}</Typography>
                </Paper>
              ) : (
                <>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Citable</TableCell>
                        <TableCell>Tags</TableCell>
                        <TableCell>Purpose</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedData?.nodes?.map((node) => (
                        <TableRow key={node.id}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {node.title}
                              {node.verified && (
                                <VerifiedIcon
                                  className={classes.verifiedIcon}
                                  fontSize="small"
                                  style={{ marginLeft: 8 }}
                                />
                              )}
                              {!node.embeddingGenerated && (
                                <WarningIcon
                                  className={classes.warningIcon}
                                  fontSize="small"
                                  style={{ marginLeft: 8 }}
                                  title="No embedding generated"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {node.category && (
                              <Chip
                                label={node.category}
                                size="small"
                                className={classes.categoryChip}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={node.status}
                              size="small"
                              color={getStatusColor(node.status)}
                              className={classes.statusChip}
                            />
                          </TableCell>
                          <TableCell>
                            {node.citable ? (
                              <CitableIcon
                                fontSize="small"
                                style={{ color: '#2196f3' }}
                                title="Document can be cited"
                              />
                            ) : (
                              <span style={{ color: '#9e9e9e' }}>—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {node.tags?.map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                className={classes.tagChip}
                              />
                            ))}
                          </TableCell>
                          <TableCell>{node.documentPurpose}</TableCell>
                          <TableCell>
                            {new Date(node.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(node)}
                              title="Edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleRegenerateEmbedding(node.id)}
                              title="Regenerate Embedding"
                              disabled={node.status === 'processing'}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(node)}
                              title="Delete"
                              color="secondary"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={pagedData?.totalCount || 0}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                  />
                </>
              )}
            </Box>
          </>
        )}

        {selectedTab === 1 && (
          <Box p={3}>
            <Typography variant="h6">Documents Pending Review</Typography>
            {/* Implementation for pending review documents */}
          </Box>
        )}

        {selectedTab === 2 && (
          <Box p={3}>
            <Typography variant="h6">Category Management</Typography>
            {/* Implementation for category management */}
          </Box>
        )}

        {selectedTab === 3 && (
          <Box className={classes.searchSection}>
            <Typography variant="h6" gutterBottom>
              Test RAG Search
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Enter a query to see which documents would be retrieved and their similarity scores.
            </Typography>

            <Box display="flex" gap={2} alignItems="flex-start" mb={3}>
              <TextField
                label="Search Query"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                value={testSearchQuery}
                onChange={(e) => setTestSearchQuery(e.target.value)}
                className={classes.searchInput}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleTestSearch();
                  }
                }}
              />
              <FormControl variant="outlined" style={{ minWidth: 100 }}>
                <InputLabel>Limit</InputLabel>
                <Select
                  value={testSearchLimit}
                  onChange={(e) => setTestSearchLimit(e.target.value)}
                  label="Limit"
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                onClick={handleTestSearch}
                disabled={searchLoading || !testSearchQuery.trim()}
                startIcon={searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
              >
                Search
              </Button>
            </Box>

            {searchLoading && (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            )}

            {searchData?.ragTestSearch && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Found {searchData.ragTestSearch.length} results
                </Typography>

                {searchData.ragTestSearch.map((result, index) => (
                  <Paper key={result.document.id} className={classes.searchResult} elevation={1}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                            #{index + 1} - {result.document.title}
                          </Typography>
                          {result.document.verified && (
                            <VerifiedIcon className={classes.verifiedIcon} fontSize="small" />
                          )}
                          {result.document.citable && (
                            <CitableIcon fontSize="small" style={{ color: '#2196f3' }} />
                          )}
                          {result.matchedViaChunks && (
                            <Chip label="Chunk Match" size="small" color="secondary" />
                          )}
                        </Box>
                        <Box display="flex" gap={1} mt={0.5} mb={1}>
                          {result.document.category && (
                            <Chip label={result.document.category} size="small" />
                          )}
                          <Chip
                            label={result.document.status}
                            size="small"
                            color={result.document.status === 'active' ? 'primary' : 'default'}
                          />
                          {result.document.tags?.slice(0, 3).map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))}
                          {result.document.metadata?.chunk_count > 1 && (
                            <Chip
                              label={`${result.document.metadata.chunk_count} chunks`}
                              size="small"
                              variant="outlined"
                              style={{ borderColor: '#9c27b0' }}
                            />
                          )}
                        </Box>
                        <Typography variant="body2" className={classes.documentBody}>
                          {result.document.body?.substring(0, 300)}
                          {result.document.body?.length > 300 ? '...' : ''}
                        </Typography>
                      </Box>
                      <Box textAlign="right" ml={2} minWidth={120}>
                        <Typography variant="h6" className={classes.similarityScore}>
                          {(result.similarityScore * 100).toFixed(1)}%
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          overall
                        </Typography>
                        {result.bestChunkSimilarity != null && (
                          <Typography variant="caption" style={{ color: '#9c27b0' }} display="block">
                            chunk: {(result.bestChunkSimilarity * 100).toFixed(1)}%
                          </Typography>
                        )}
                        {result.documentSimilarity != null && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            doc: {(result.documentSimilarity * 100).toFixed(1)}%
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                ))}

                {searchData.ragTestSearch.length === 0 && (
                  <Typography color="textSecondary">
                    No documents found matching your query.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Dialogs */}
      <VectorDocumentForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        onSubmit={(values) => {
          if (selectedDocument) {
            updateDocument({ variables: { id: selectedDocument.id, ...values } });
          } else {
            createDocument({ variables: values });
          }
        }}
        categories={categoriesData?.vectorDocumentCategories || []}
      />

      <UrlProcessorDialog
        open={urlDialogOpen}
        onClose={() => setUrlDialogOpen(false)}
        onSubmit={(urls, metadata) => {
          return processUrls({ variables: { urls, ...metadata } });
        }}
        onSuccess={(message) => {
          showSnackbar(message || 'URLs queued for background processing. You will be notified when complete.', 'success');
          setUrlDialogOpen(false);
          refetch();
        }}
      />

      <BulkImportDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onSubmit={(options) => {
          return bulkImportDocuments(options);
        }}
        categories={categoriesData?.vectorDocumentCategories || []}
      />

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{documentToDelete?.title}"? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RagAdminDashboard;
